<script setup lang="tsx">
import type { PasswordLogin } from '@/models/api-generated'
import http from '@/services/http'
import { computed, ref } from 'vue'

const props = defineProps<{
	firstLogin: boolean
}>()
const emit = defineEmits(['success'])
const email = ref('')
const firstname = ref('')
const lastname = ref('')
const password = ref('')
const password2 = ref('')
const isBusy = ref(false)

const isValidEmail = computed(() => email.value.length > 2 && email.value.includes('@')) //computed(() => /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email.value))
const isValidFirstname = computed(() => !props.firstLogin || firstname.value.length > 0)
const isValidLastname = computed(() => !props.firstLogin || lastname.value.length > 0)
const isValidPassword = computed(() => password.value.length >= 12)
const isValidPassword2 = computed(() => !props.firstLogin || password.value === password2.value)
const isValid = computed(
	() => isValidEmail.value && isValidFirstname.value && isValidLastname.value && isValidPassword.value && isValidPassword2.value,
)

async function login() {
	if (!isValid.value) return
	const request: PasswordLogin | any = {
		email: email.value,
		password: password.value,
	}

	if (firstname.value) request.firstname = firstname.value
	if (lastname.value) request.lastname = lastname.value

	isBusy.value = true
	try {
		const { status } = await http.post('login', request)
		if (status == 200) emit('success')
	} catch {
		// ignore
	}
	isBusy.value = false
}
</script>

<template>
	<template v-if="props.firstLogin">
		<input name="disable-pwd-mgr-1" type="password" id="disable-pwd-mgr-1" style="display: none" value="disable-pwd-mgr-1" />
		<input name="disable-pwd-mgr-2" type="password" id="disable-pwd-mgr-2" style="display: none" value="disable-pwd-mgr-2" />
		<input name="disable-pwd-mgr-3" type="password" id="disable-pwd-mgr-3" style="display: none" value="disable-pwd-mgr-3" />

		<div class="row mb-3">
			<div class="col-md-4 text-start text-nowrap">
				<label for="user-firstname" class="col-form-label">{{ $t('firstname') }}</label>
			</div>
			<div class="col-md-8">
				<input
					id="user-firstname"
					type="text"
					maxlength="100"
					class="form-control"
					:class="{ 'is-invalid': !isValidFirstname }"
					v-model="firstname"
					@keydown.enter.prevent="login"
				/>
			</div>
		</div>
		<div class="row mb-3">
			<div class="col-md-4 text-start text-nowrap">
				<label for="user-lastname" class="col-form-label">{{ $t('lastname') }}</label>
			</div>
			<div class="col-md-8">
				<input
					id="user-lastname"
					type="text"
					maxlength="100"
					class="form-control"
					:class="{ 'is-invalid': !isValidLastname }"
					v-model="lastname"
					@keydown.enter.prevent="login"
				/>
			</div>
		</div>
	</template>
	<div class="row mb-3">
		<div class="col-md-4 text-start">
			<label for="user-email" class="col-form-label">{{ $t('email') }}</label>
		</div>
		<div class="col-md-8">
			<input
				id="user-email"
				type="email"
				maxlength="60"
				class="form-control"
				:class="{ 'is-invalid': !isValidEmail }"
				v-model="email"
				@keydown.enter.prevent="login"
			/>
		</div>
	</div>
	<div class="row mb-3">
		<div class="col-md-4 text-start">
			<label for="user-password" class="col-form-label">{{ $t('password') }}</label>
		</div>
		<div class="col-md-8">
			<input
				id="user-password"
				type="password"
				maxlength="60"
				class="form-control"
				:class="{ 'is-invalid': !isValidPassword }"
				v-model="password"
				@keydown.enter.prevent="login"
			/>
		</div>
	</div>
	<template v-if="props.firstLogin">
		<div class="row mb-3">
			<div class="col-md-4 text-start text-nowrap">
				<label for="user-password2" class="col-form-label">{{ $t('password') }} x2</label>
			</div>
			<div class="col-md-8">
				<input
					id="user-password2"
					type="password"
					maxlength="60"
					class="form-control"
					:class="{ 'is-invalid': !isValidPassword2 }"
					v-model="password2"
					@keydown.enter.prevent="login"
				/>
			</div>
		</div>
	</template>
	<div class="row">
		<div class="col">
			<button id="login-btn" class="btn btn-primary w-100" :disabled="isBusy || !isValid" @click="login">
				<template v-if="isBusy">
					<div class="spinner-border spinner-border-sm"></div>
				</template>
				<template v-else>{{ $t('login') }}</template>
			</button>
		</div>
	</div>
</template>

<style lang="sass" scoped></style>
